// apps/prompts/src/App.tsx
import { ComingSoonGate, ComingSoonPage, useAuthStore } from '@neuroforge/shared/client'
import { LoadingSpinner } from '@neuroforge/ui'
import { useQuery } from '@tanstack/react-query'
import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Outlet } from 'react-router'
import { RouterProvider } from 'react-router/dom'

const COMING_SOON = import.meta.env.VITE_COMING_SOON === 'true'

import AdminLayout from './components/admin/AdminLayout'
import AdminRoute from './components/admin/AdminRoute'
import ErrorBoundary from './components/ErrorBoundary'
import {
  PromptErrorFallback,
  CollectionErrorFallback,
  PageErrorFallback,
} from './components/ErrorFallbacks'
import PromptsLayout from './components/PromptsLayout'
import VantaBackground from './components/VantaBackground'
import { adminSessionKey, fetchAdminSession, isVerifiedAdmin } from './lib/admin-session'
import { authPrincipalKey, sessionTokenTag } from './lib/auth-query-policy'

// Lazy loading des pages
const HomePage = lazy(() => import('./pages/HomePage'))
const BrowsePage = lazy(() => import('./pages/BrowsePage'))
const PromptDetailPage = lazy(() => import('./pages/PromptDetailPage'))
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'))
// ProfilePage est dans shared — pas de fichier local
// ProfilePage shared avec prop app pour conditionner les liens
const ProfilePage = lazy(() =>
  import('@neuroforge/shared/client').then((m) => ({
    default: () => m.ProfilePage({ app: 'prompts' }),
  })),
)
const CollectionsPage = lazy(() => import('./pages/CollectionsPage'))
const CollectionDetailPage = lazy(() => import('./pages/CollectionDetailPage'))
const SharedCollectionPage = lazy(() => import('./pages/SharedCollectionPage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))
// Patch V: Mes Prompts (forks + custom + demande publication)
const MyPromptsPage = lazy(() => import('./pages/MyPromptsPage'))
const EditMyPromptPage = lazy(() => import('./pages/EditMyPromptPage'))
const MyPromptShowPage = lazy(() => import('./pages/MyPromptShowPage'))
// Callback local obligatoire : le Hub y transmet #nf_token, sans le placer en query.
const AuthCallbackPage = lazy(() =>
  import('@neuroforge/shared/client').then((m) => ({ default: m.AuthCallbackPage })),
)
// Les autres routes /auth/* sont centralisées sur le Hub.
const HubAuthRedirect = lazy(() =>
  import('@neuroforge/shared/client').then((m) => ({ default: m.HubAuthRedirect })),
)
const NotFoundPage = lazy(() =>
  import('@neuroforge/shared/client').then((m) => ({
    default: () => m.NotFoundPage({ app: 'prompts' }),
  })),
)
// Admin pages (Batch O)
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'))
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'))
const AdminPromptsPage = lazy(() => import('./pages/admin/AdminPromptsPage'))
const AdminCommentsPage = lazy(() => import('./pages/admin/AdminCommentsPage'))
const AdminCreatePromptPage = lazy(() => import('./pages/admin/AdminCreatePromptPage'))
const AdminPromptPreviewPage = lazy(() => import('./pages/admin/AdminPromptPreviewPage'))
// C3: Admin contribution — gestion granulaire des cas d'usage
const AdminPromptUseCasesPage = lazy(() => import('./pages/admin/AdminPromptUseCasesPage'))
// D1: modération des soumissions prompts
const AdminSubmissionsPage = lazy(() => import('./pages/admin/AdminSubmissionsPage'))
const AdminFeedbackPage = lazy(() => import('./pages/admin/AdminFeedbackPage'))
// Création d'un prompt user (brouillon privé + soumission optionnelle)
const CreateMyPromptPage = lazy(() => import('./pages/CreateMyPromptPage'))
const PublicProfilePage = lazy(() => import('./pages/PublicProfilePage'))
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'))

function withPageBoundary(element: ReactNode) {
  return (
    <ErrorBoundary fallback={(err, reset) => <PageErrorFallback error={err} onReset={reset} />}>
      {element}
    </ErrorBoundary>
  )
}

const router = createBrowserRouter([
  // Route hors ComingSoonGate : elle seule doit recevoir le handoff Hub.
  { path: '/auth/callback', element: <AuthCallbackPage />, errorElement: <NotFoundPage /> },
  { path: '/auth/*', element: <HubAuthRedirect />, errorElement: <NotFoundPage /> },
  {
    element: <PromptsSiteGate />,
    children: [
      {
        element: <PromptsLayout />,
        errorElement: <NotFoundPage />,
        children: [
          {
            path: '/',
            element: <HomePage />,
          },
          {
            path: '/browse',
            element: (
              <ErrorBoundary>
                <BrowsePage />
              </ErrorBoundary>
            ),
          },
          {
            // Legacy : /prompts/:slug (ou /prompts/:id) — PromptDetailPage redirige
            // vers l'URL canonique /prompts/:id/:slug une fois le prompt résolu.
            path: '/prompts/:idOrSlug',
            element: (
              <ErrorBoundary
                fallback={(err, reset) => <PromptErrorFallback error={err} onReset={reset} />}
              >
                <PromptDetailPage />
              </ErrorBoundary>
            ),
          },
          {
            // URL canonique : id (unicité + résolution) + slug (SEO).
            path: '/prompts/:idOrSlug/:slug',
            element: (
              <ErrorBoundary
                fallback={(err, reset) => <PromptErrorFallback error={err} onReset={reset} />}
              >
                <PromptDetailPage />
              </ErrorBoundary>
            ),
          },
          {
            path: '/favorites',
            element: withPageBoundary(<FavoritesPage />),
          },
          {
            path: '/profile',
            element: withPageBoundary(<ProfilePage />),
          },
          {
            path: '/collections',
            element: withPageBoundary(<CollectionsPage />),
          },
          {
            path: '/notifications',
            element: withPageBoundary(<NotificationsPage />),
          },
          {
            path: '/my-prompts',
            element: withPageBoundary(<MyPromptsPage />),
          },
          {
            path: '/my-prompts/:id/show',
            element: withPageBoundary(<MyPromptShowPage />),
          },
          {
            path: '/my-prompts/:id/edit',
            element: withPageBoundary(<EditMyPromptPage />),
          },
          {
            path: '/my-prompts/new',
            element: withPageBoundary(<CreateMyPromptPage />),
          },
          {
            path: '/users/:username',
            element: withPageBoundary(<PublicProfilePage />),
          },
          {
            path: '/leaderboard',
            element: withPageBoundary(<LeaderboardPage />),
          },
          {
            path: '/collections/shared/:token',
            element: withPageBoundary(<SharedCollectionPage />),
          },
          {
            path: '/collections/:slug',
            element: (
              <ErrorBoundary
                fallback={(err, reset) => <CollectionErrorFallback error={err} onReset={reset} />}
              >
                <CollectionDetailPage />
              </ErrorBoundary>
            ),
          },
          {
            path: '*',
            element: <NotFoundPage />,
          },
        ],
      },
      // Admin routes (Batch O — layout dédié + guard ADMIN)
      {
        element: <AdminRoute />,
        errorElement: <NotFoundPage />,
        children: [
          {
            element: withPageBoundary(<AdminLayout />),
            children: [
              { path: '/admin', element: <AdminDashboardPage /> },
              { path: '/admin/users', element: <AdminUsersPage /> },
              { path: '/admin/prompts', element: <AdminPromptsPage /> },
              { path: '/admin/prompts/create', element: <AdminCreatePromptPage /> },
              { path: '/admin/prompts/:slug/preview', element: <AdminPromptPreviewPage /> },
              // C3: gestion granulaire des cas d'usage d'un prompt
              {
                path: '/admin/prompts/:slug/use-cases',
                element: <AdminPromptUseCasesPage />,
              },
              { path: '/admin/submissions', element: <AdminSubmissionsPage /> },
              { path: '/admin/comments', element: <AdminCommentsPage /> },
              { path: '/admin/feedback', element: <AdminFeedbackPage /> },
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])

function AppFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  )
}

/**
 * L'auth bootstrap protège les URL profondes : ni redirection prématurée ni
 * requête de données privées avant la résolution du refresh initial.
 * Le key remonte les pages contenant un état local lors d'un changement de
 * principal, de rôle, d'état ou de tier (la recherche publique reste en cache).
 */
function PromptsSiteGate() {
  const { isLoading, isAuthenticated, user, accessToken } = useAuthStore()
  const adminBypass = COMING_SOON && isAuthenticated && user?.role === 'ADMIN' && !!accessToken
  const adminCheck = useQuery({
    queryKey: adminSessionKey(user?.id, accessToken),
    queryFn: ({ signal }) => fetchAdminSession(signal),
    enabled: adminBypass && !isLoading,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  })
  if (isLoading || (adminBypass && (adminCheck.isPending || adminCheck.isFetching))) {
    return <AppFallback />
  }
  // Un ancien ADMIN déclassé ne doit pas contourner Coming Soon sur la base
  // du seul store Shared (la DB contrôlée par /auth/me fait autorité).
  if (adminBypass && !isVerifiedAdmin(adminCheck.data, user!.id)) {
    return <ComingSoonPage app="prompts" hubUrl={import.meta.env.VITE_HUB_URL || '/'} />
  }

  return (
    <ComingSoonGate
      app="prompts"
      enabled={COMING_SOON}
      hubUrl={import.meta.env.VITE_HUB_URL || '/'}
    >
      <Outlet
        key={JSON.stringify([
          authPrincipalKey({ isAuthenticated, user }),
          sessionTokenTag(accessToken),
        ])}
      />
    </ComingSoonGate>
  )
}

export default function App() {
  return (
    <>
      <VantaBackground />
      <Suspense fallback={<AppFallback />}>
        <RouterProvider router={router} />
      </Suspense>
    </>
  )
}
